import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import api from '../../api/config';
import { LEASES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import {
  buildLeaseRelationParams,
  formatLeaseDate,
  getLeaseAuthorLabel,
  getLeaseStatusLabel,
} from './leaseApiHelpers';
import { LEASE_PERMISSIONS } from './leasePermissions';

const row = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const ViewLease = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lease, setLease] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(LEASES.BY_ID(id), {
          params: buildLeaseRelationParams(),
        });
        const payload = data?.data ?? data;
        if (!cancelled) setLease(payload);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load lease');
        navigate('/leases');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !lease) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/leases')} sx={{ mb: 2 }}>
          Back to leases
        </Button>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {lease.name ?? 'Lease'}
          </Typography>
          <Can permission={LEASE_PERMISSIONS.EDIT}>
            <Button variant="outlined" size="small" onClick={() => navigate(`/leases/edit/${id}`)}>
              Edit
            </Button>
          </Can>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {row('Name', lease.name)}
              {row('Status', getLeaseStatusLabel(lease))}
              {row('Author', getLeaseAuthorLabel(lease))}
              {row('Created at', formatLeaseDate(lease.created_at))}
              {row('Description', lease.description ?? '—')}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewLease;
